/**
 * 设备检测工具函数
 * 统一项目中的移动端检测逻辑
 */

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouchDevice: boolean;
  screenWidth: number;
  screenHeight: number;
  userAgent: string;
}

/**
 * 检测是否为桌面设备
 * 通过多个维度综合判断,减少误判
 */
export function isDesktopDevice(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();

  // 1. 优先检测明确的桌面操作系统
  const hasDesktopOS = /(windows nt|macintosh|linux x86_64|linux i686)/i.test(
    userAgent
  );

  // 2. 排除移动设备UA
  const hasMobileUA =
    /(android|webos|iphone|ipod|blackberry|iemobile|opera mini)/i.test(
      userAgent
    );

  // 3. 检测是否有鼠标指针(最可靠的特征)
  const hasMousePointer = window.matchMedia("(pointer: fine)").matches;

  // 4. 检测主要输入设备是否支持悬停
  const canHover = window.matchMedia("(hover: hover)").matches;

  // 5. 检测是否为iPad(容易误判为桌面)
  const isIPad =
    /ipad/i.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  // 6. 检测屏幕方向锁定API(移动设备特有)
  const hasMobileOrientation =
    "orientation" in window && typeof window.orientation === "number";

  // 综合判断
  return (
    hasDesktopOS &&
    !hasMobileUA &&
    !isIPad &&
    (hasMousePointer || canHover) &&
    !hasMobileOrientation
  );
}

/**
 * 检测是否为移动设备(手机)
 */
export function isMobileDevice(): boolean {
  // 如果是桌面设备,直接返回false
  if (isDesktopDevice()) {
    return false;
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const screenWidth = window.innerWidth;

  // 1. 明确的手机UA
  const isMobileUA =
    /(android|webos|iphone|ipod|blackberry|iemobile|opera mini)/i.test(
      userAgent
    ) && !/tablet|ipad/i.test(userAgent);

  // 2. 检测粗糙的指针(触摸屏)
  const hasCoarsePointer = window.matchMedia("(pointer: coarse)").matches;

  // 3. 小屏幕 + 竖屏为主
  const isSmallScreen = screenWidth < 768;
  const isPortrait = window.matchMedia("(orientation: portrait)").matches;

  return isMobileUA || (hasCoarsePointer && isSmallScreen && isPortrait);
}

/**
 * 检测是否为平板设备
 */
export function isTabletDevice(): boolean {
  // 如果是桌面设备,直接返回false
  if (isDesktopDevice()) {
    return false;
  }

  // 如果是手机,也返回false
  if (isMobileDevice()) {
    return false;
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const screenWidth = window.innerWidth;

  // 1. 明确的平板UA
  const isTabletUA =
    /(ipad|tablet|playbook|silk)/i.test(userAgent) ||
    (/android/i.test(userAgent) && !/mobile/i.test(userAgent));

  // 2. iPad特殊检测
  const isIPad =
    /ipad/i.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  // 3. 粗糙指针 + 中等屏幕尺寸
  const hasCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const isMediumScreen = screenWidth >= 768 && screenWidth < 1024;

  return isTabletUA || isIPad || (hasCoarsePointer && isMediumScreen);
}

/**
 * 检测是否支持触摸
 */
export function isTouchDevice(): boolean {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}

/**
 * 获取详细的设备信息
 */
export function getDeviceInfo(): DeviceInfo {
  return {
    isMobile: isMobileDevice(),
    isTablet: isTabletDevice(),
    isDesktop: isDesktopDevice(),
    isTouchDevice: isTouchDevice(),
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    userAgent: navigator.userAgent.toLowerCase()
  };
}

/**
 * 响应式设备检测 Hook
 * 用于Vue组合式API中
 */
export function useDeviceDetection() {
  const deviceInfo = ref(getDeviceInfo());

  const updateDeviceInfo = () => {
    deviceInfo.value = getDeviceInfo();
  };

  onMounted(() => {
    window.addEventListener("resize", updateDeviceInfo);
    window.addEventListener("orientationchange", updateDeviceInfo);
  });

  onUnmounted(() => {
    window.removeEventListener("resize", updateDeviceInfo);
    window.removeEventListener("orientationchange", updateDeviceInfo);
  });

  return {
    deviceInfo: readonly(deviceInfo),
    isMobile: computed(() => deviceInfo.value.isMobile),
    isTablet: computed(() => deviceInfo.value.isTablet),
    isDesktop: computed(() => deviceInfo.value.isDesktop),
    isTouchDevice: computed(() => deviceInfo.value.isTouchDevice)
  };
}

// Vue 3 组合式API的导入
import { ref, computed, readonly, onMounted, onUnmounted } from "vue";
